import { useCallback, useMemo, useRef } from 'react';

// Phase 1 event taxonomy for the Invest redesign. Strict union keeps payloads consistent
// across surfaces and gives the data team a single source of truth.
type AnalyticsEvent =
  | 'row_impression'
  | 'row_hidden_no_eligible'
  | 'card_view'
  | 'card_open'
  | 'modal_open'
  | 'modal_section_view'
  | 'modal_scroll_depth'
  | 'cta_click_primary'
  | 'cta_click_secondary'
  | 'filter_apply'
  | 'watchlist_add'
  | 'watchlist_remove'
  | 'parcel_tier_change'
  | 'strategy_commit_started'
  | 'strategy_commit_confirmed'
  | 'strategy_commit_abandoned'
  | 'view_all_click';

interface AnalyticsPayload {
  source_row?: string;
  wine_id?: string;
  parcel_id?: string;
  strategy_id?: string;
  card_position?: number;
  section_id?: string;
  scroll_depth_pct?: number;
  // Free-form bag for event-specific extras. Keep keys snake_case.
  [k: string]: string | number | boolean | null | undefined;
}

interface AnalyticsWindow {
  heap?: { track?: (event: string, props?: Record<string, unknown>) => void };
  dataLayer?: Array<Record<string, unknown>>;
}

/**
 * Fires events to Heap (window.heap.track) AND the GTM dataLayer (window.dataLayer.push).
 * Belt-and-braces per discovery #9. Both calls are no-ops if the targets aren't loaded yet,
 * so the helper is always safe to call.
 */
export function useAnalytics() {
  const sessionIdRef = useRef<string>();
  if (!sessionIdRef.current) {
    sessionIdRef.current = `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  }

  // Member tier isn't surfaced on accountInfo in Phase 1; wire it through this memo when it lands
  // and it flows into every event automatically.
  const memberTier = useMemo<string | undefined>(() => undefined, []);

  const track = useCallback(
    (event: AnalyticsEvent, payload: AnalyticsPayload = {}) => {
      const win = window as unknown as AnalyticsWindow;
      const enriched = {
        event,
        member_tier: memberTier ?? null,
        session_id: sessionIdRef.current,
        ts: Date.now(),
        ...payload,
      };

      try {
        win.heap?.track?.(event, enriched);
      } catch {
        // Heap failure must never break the host UI.
      }

      try {
        win.dataLayer = win.dataLayer || [];
        win.dataLayer.push(enriched);
      } catch {
        // Same for dataLayer.
      }
    },
    [memberTier],
  );

  return { track };
}
